import Btn from 'components/buttons/Btn'
import ConfirmModal from 'components/ConfirmModal'
import I from 'components/I'
import Icon from 'components/Icon'
import Tip from 'components/Tip'
import OrderContext from 'pages/Order/_context'
import PropTypes from 'prop-types'
import { useContext } from 'react'
import { Badge } from 'react-bootstrap'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useUser } from 'state/Auth'
import OrderItem from 'state/models/OrderItem'
import { removeAdjustment } from 'state/Orders'
import { FMT, klass, useModalState } from 'utils'

/** @param item {OrderItem} */
export default function ItemRow({ item }) {
	const { t } = useTranslation()

	const adjustments = useContext(OrderContext).getAdjustmentsFor(item)
	const totalExtras = item.add_ons.length + adjustments.length

	// t('Item Adjustment')
	return <>
		<tr {...klass({ hasAddOns: totalExtras })}>
			<td colSpan={2}>
				<div className="YAAF">
          <span className="qty">
            <tt>{item.quantity}</tt>
            <span className="text-muted">×</span>
          </span>&nbsp;
					{item.display_name_with_HTML}&nbsp;
					{item.menu_name && item.menu_name != item.display_name && <I force>({item.menu_name})</I>}
				</div>

				{item.menubuddy && <p className="buddy">
					<Icon fixedWidth i="user-tag"/> {t('for {{buddy}}', { buddy: item.menubuddy })}
				</p>}
				{item.special_instructions && <p className={item.is_open_item? 'adjustment' : 'note'}>
					{item.is_open_item? <AdjustmentBadge/> : <Icon fixedWidth i="exclamation-circle"/>}{' '}
					{item.is_open_item? t(item.special_instructions) : item.special_instructions}
				</p>}
			</td>

			<td>
				{FMT.money(item.price_value * item.quantity) /* TODO maybe we should start using item.line_total instead? */}
				{item.quantity > 1 && <small>{item.quantity}× {FMT.money(item.price_value)}</small>}
				{/* even though this quantity line may cause unneeded space below the item name, we can't use rowspan here because some add-ons have their own pricing */}

				{item.is_open_item? <DeleteAdjustment item={item}/> : null}
			</td>
		</tr>

		{item.add_ons.map((addOn, i) => (
			<tr {...klass('add-on', { last: (i + 1 == totalExtras) })} key={addOn.id}>
				<td colSpan={2}>
					<Icon i="plus" fixedWidth className="text-warning"/>{' '}
					{addOn.full_display_name}{' '}
					{addOn.price_value && item.quantity > 1? <small>({item.quantity}× {FMT.money(addOn.price_value)})</small> : null} {/* eslint-disable-line i18next/no-literal-string */}
				</td>
				<td>{addOn.price_value? FMT.money(addOn.price_value * item.quantity) : null}</td>
			</tr>
		))}

		{adjustments.map(/** @type Item */ (adj, i) => (
			<tr {...klass('add-on', { last: (i + item.add_ons.length + 1 == totalExtras) })} key={adj.id}>
				<td colSpan={2}>
					<AdjustmentBadge/>{' '}
					{adj.special_instructions}{' '}
					<DeleteAdjustment item={adj}/>
				</td>
				<td>{adj.price_value? FMT.money(adj.price_value * adj.quantity) : null}</td>
			</tr>
		))}
	</>
}

ItemRow.propTypes = {
	item: PropTypes.instanceOf(OrderItem).isRequired,
}

function DeleteAdjustment({ item }) {
	const { t }      = useTranslation()
	const dispatch   = useDispatch()
	const modalState = useModalState()
	const order      = useContext(OrderContext)

	async function doRemoveAdjustment() {
		await dispatch(removeAdjustment(order.id, item.id))
	}

	if (!useUser().useCanAdjustOrders(order)) {
		return null
	}

	return <>
		<Btn icon="trash" size="sm" variant="outline-danger" onClick={modalState.doOpen}/>
		<ConfirmModal {...modalState} dangerous doSubmit={doRemoveAdjustment}
		              confirmIcon="trash" confirmTexts={[t('Remove'), t('Removing')]} cancelGoBack
		              title={t('Remove it?')}>
			{item.isSubAdjustment?
				<Trans>
					Please, confirm the removal of the <u><i>{{ name: item.special_instructions }}</i></u>{' '}
					adjustment to the <u><I en>{{ mainItem: item.display_name_without_HTML }}</I></u>.
				</Trans>
			:
				<Trans>
					Please, confirm the removal of the{' '}
					<u><I en>{{ name: item.display_name_without_HTML }}</I></u> adjustment.
				</Trans>
			}
		</ConfirmModal>
	</>
}

//TODO this tooltip could be a good place to give some extra information: when the adjustment was created, or by whom
function AdjustmentBadge() {
	const { t } = useTranslation()
	return <Tip text={t('Adjustment')} placement="top-end">
		<Badge variant="info">{t('Adj.', { context: 'adjustment' })}</Badge>
	</Tip>
}
